// src/utils/sentencePool.js
// Welche Sätze aus <lang>_sentences_cloze.csv in einer Woche drankommen dürfen
// (Grammatik-Stufe, Zeitformen, bekannte Wörter). Gemeinsam genutzt von
// Lückentexten und Freier Übersetzung.
import {
  tokenizeTarget,
  analyzeTokens,
  normalizeToken,
} from "./sentenceClozeHelpers";
import { buildKnownWordsFromWeeklySetsAndVerbs, expandWordForms } from "./knownWordsBuilder";
import { parseLanguageId } from "../data/languages";
import { getAllowedTenses, getCourseProgress, TENSES } from "../data/tenseSchedule";
import { getTopicKeysUpToWeek } from "../data/phraseSchedule";

/**
 * Höchste erlaubte Grammatik-Stufe (morph_axis):
 * 0 = nur Präsens, 1 = mit Infinitiv ("quiero ver"), 2/3 = weitere Zeitformen.
 * Bis zur ersten neuen Zeitform (Perfecto) nur Stufe 0; danach entscheiden allein
 * die Zeitform-Spalten (getAllowedTensesForWeek), welche Sätze passen.
 */
export function getMaxMorphAxisForWeek(weekIndex, pace, lang) {
  return getAllowedTenses(weekIndex, pace, lang).perfect ? 3 : 0;
}

// Basis-Wörter, die je Lernsprache immer als "known" gelten sollen
// (werden per normalizeToken normalisiert)
const SPANISH_ALWAYS_KNOWN_RAW = [
  // Subjektpronomen
  "yo",
  "tú",
  "tu", // falls ohne Akzent auftaucht
  "él",
  "el", // falls ohne Akzent
  "ella",
  "nosotros",
  "nosotras",
  "vosotros",
  "vosotras",
  "ellos",
  "ellas",
  "usted",
  "ustedes",

  // Objekt-/Reflexivpronomen
  "me",
  "te",
  "se",
  "nos",
  "os",
  "lo",
  "la",
  "los",
  "las",
  "le",
  "les",

  // Unbestimmte Artikel, Kontraktionen und "que" (fehlen in den Vokabeln, stehen aber in fast jedem Satz)
  "un",
  "una",
  "unos",
  "unas",
  "al",
  "del",
  "que",
];

// Französisch: Pronomen, Artikel samt Kontraktionen und verkürzten Formen (j', l', qu' …),
// Verneinung "ne"; stehen in fast jedem Satz, fehlen aber teils in den Vokabeln
const FRENCH_ALWAYS_KNOWN_RAW = [
  // Subjektpronomen
  "je", "j'", "tu", "il", "elle", "on", "nous", "vous", "ils", "elles",
  // Objekt-/Reflexivpronomen, betonte Pronomen
  "me", "m'", "te", "t'", "se", "s'", "le", "la", "l'", "les", "lui", "leur", "y", "en",
  "moi", "toi", "soi", "eux",
  // Artikel und Kontraktionen
  "un", "une", "des", "du", "de", "d'", "au", "aux",
  // Verneinung, "que"
  "ne", "n'", "que", "qu'",
];

const ALWAYS_KNOWN_RAW = { es: SPANISH_ALWAYS_KNOWN_RAW, fr: FRENCH_ALWAYS_KNOWN_RAW };

// Präsensformen von "haber" (als Hilfsverb fürs Perfekt) – normalisiert
const HABER_AUX_FORMS_NORMALIZED = [
  "he",
  "has",
  "ha",
  "hemos",
  "habeis",
  "han",
];

/**
 * Safeguard: Prüft, ob ein Satz nur "einfaches" Präsens verwendet.
 */
export function isPresentOnlySentence(sentence, verbFormsSet) {
  if (!sentence || !sentence.target || !verbFormsSet || verbFormsSet.size === 0) {
    return true;
  }

  try {
    const tokens = tokenizeTarget(sentence.target, sentence.lang);
    const roles = analyzeTokens(tokens, verbFormsSet, sentence.lang);

    for (let i = 0; i < tokens.length; i++) {
      if (roles[i] === "verb") {
        const norm = normalizeToken(tokens[i]);
        if (!norm) return false;

        // Perfekt-Guard
        if (HABER_AUX_FORMS_NORMALIZED.includes(norm)) {
          return false;
        }

        if (!verbFormsSet.has(norm)) {
          return false;
        }
      }
    }

    return true;
  } catch (e) {
    console.warn("sentencePool: isPresentOnlySentence failed:", e);
    return true;
  }
}

// -------- Tense-Filter auf Basis der CSV-Flags --------

/** Erlaubte Zeitformen in Woche `weekIndex`, Zeitplan in tenseSchedule.js. */
export function getAllowedTensesForWeek(weekIndex, pace, lang) {
  return getAllowedTenses(weekIndex, pace, lang);
}

/** Ein Satz passt, wenn jede Zeitform, die er enthält (tense_* = 1), erlaubt ist (fehlt sie im Zeitplan der Lernsprache: nie). */
export function sentenceMatchesTenseRules(sentence, rules) {
  if (!sentence || !rules) return true;

  return TENSES.every((tense) => {
    const num = Number(sentence[`tense_${tense}`]);
    return num !== 1 || rules[tense] === true;
  });
}

/**
 * Sätze, die in Woche `weekIndex` beim Tempo `pace` passen (Grammatik-Stufe + Zeitformen).
 * Sätze aus `excludedIds` (zuletzt gesehen) fallen raus, solange noch andere übrig sind.
 * Findet sich nichts, werden die Regeln schrittweise gelockert.
 */
export function filterSentencesForWeek(
  sentences,
  weekIndex,
  pace,
  verbFormsSet,
  excludedIds = [],
  lang
) {
  const all = sentences || [];
  const maxMorphAxis = getMaxMorphAxisForWeek(weekIndex || 1, pace, lang);
  const allowedTenseRules = getAllowedTensesForWeek(weekIndex || 1, pace, lang);

  const excluded = new Set(excludedIds);
  const primaryPool = all.filter((s) => !excluded.has(s.id));
  const poolBase = primaryPool.length > 0 ? primaryPool : all;

  const byGrammar = () => {
    let pool = poolBase.filter((s) => (s.morph_axis ?? 0) <= maxMorphAxis);
    if (maxMorphAxis === 0) {
      pool = pool.filter((s) => isPresentOnlySentence(s, verbFormsSet));
    }
    return pool;
  };

  let pool = byGrammar().filter((s) =>
    sentenceMatchesTenseRules(s, allowedTenseRules)
  );

  if (pool.length === 0) {
    pool = byGrammar();
    if (pool.length === 0) {
      pool = poolBase;
    }
  }

  return pool;
}

// Zielschwierigkeit (computeDifficultyCore: Länge, Grammatik-Stufe, Satzbau) am Kursanfang
// und am Kursende, dazwischen linear nach Kursfortschritt. Zum Vergleich: kurze Präsenssätze
// liegen um 0,2, Sätze mit Nebensatz und Subjuntivo um 0,6–0,9.
export const TARGET_DIFFICULTY_START = 0.15;
export const TARGET_DIFFICULTY_END = 0.75;

/** Zielschwierigkeit der Sätze in Woche `weekIndex` (für pickSentenceForCloze). */
export function getTargetDifficulty(weekIndex, pace) {
  const progress = Math.min(1, getCourseProgress(weekIndex, pace));
  return TARGET_DIFFICULTY_START + (TARGET_DIFFICULTY_END - TARGET_DIFFICULTY_START) * progress;
}

/**
 * Wörter der Themen-Einträge (<lang>_topics.csv: Zahlen, Wochentage, Monate, Uhrzeit), deren
 * Themen-Woche bis Woche `weekIndex` drankam (Plan in phraseSchedule.js). Diese Wörter
 * stehen nicht in den Vokabeln, sollen in Sätzen aber als bekannt gelten.
 */
export function getKnownTopicWords(topics, weekIndex, pace, lang) {
  const keys = getTopicKeysUpToWeek(weekIndex, pace, lang);
  return (topics || [])
    .filter((t) => keys.has(t.topic))
    .flatMap((t) => tokenizeTarget(t.target || "", lang));
}

/**
 * Bekannte Wörter der Lernsprache: Vokabeln aller Wochensets bis jetzt, deren Verbformen,
 * Funktionswörter, Wörter der Themen-Wochen (`topicWords`, siehe getKnownTopicWords) und
 * Pronomen/Artikel der Lernsprache, die immer als bekannt gelten (ALWAYS_KNOWN_RAW).
 * Formen nach den Regeln der Lernsprache (wordForms.js).
 */
export function buildKnownWordsSet(weeklyWords, verbs, functionWords, langId, topicWords = []) {
  // ohne Sprache: spanische Formenregeln (Standard in wordForms.js), keine Immer-bekannt-Liste
  const lang = parseLanguageId(langId)?.source;
  const augmented = new Set(
    buildKnownWordsFromWeeklySetsAndVerbs(weeklyWords || [], verbs || [], lang) || []
  );

  (ALWAYS_KNOWN_RAW[lang] || []).forEach((raw) => {
    const norm = normalizeToken(raw);
    if (norm) augmented.add(norm);
  });

  (topicWords || []).forEach((word) => {
    const norm = normalizeToken(word);
    // Mehrzahl mitnehmen (los domingos)
    if (norm) expandWordForms(norm, "topic", lang).forEach((f) => augmented.add(f));
  });

  // Funktionswörter samt Formen (todo → todos, alguno → algún, este → esta)
  (functionWords || new Set()).forEach((word) => {
    const norm = normalizeToken(word);
    if (norm) expandWordForms(norm, "other", lang).forEach((f) => augmented.add(f));
  });

  return augmented;
}
